import React from 'react'

interface StarProps {
  filled: boolean
  size?: number
  color?: string
}

// SVG star for both filled and unfilled
const Star = ({ filled, size = 20, color = '#222' }: StarProps) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill={filled ? color : 'none'}
      stroke={color}
      strokeWidth="1.5"
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
      aria-hidden="true"
    >
      <polygon
        points="10,1.5 12.8,7.3 19.1,8.0 14.0,12.4 15.5,18.5 10,15.3 4.5,18.5 6.0,12.4 0.9,8.0 7.2,7.3"
        fill={filled ? color : 'none'}
      />
    </svg>
  )
}

// package-local
interface StarRatingProps {
  value: number
  outOf?: number
  showValue?: boolean
  reviewCount?: number | string | null
  size?: number | 'small' | 'large'
  color?: string
  starStyle?: React.CSSProperties
  interactive?: boolean
  readonly?: boolean
  onChange?: (rating: number) => void
}

export const StarRating = ({
  value,
  outOf = 5,
  showValue: _showValue = false,
  reviewCount: _reviewCount = null,
  size = 20,
  color = '#ffa500',
  starStyle = {},
  interactive = false,
  readonly = false,
  onChange,
}: StarRatingProps) => {
  const [hoverValue, setHoverValue] = React.useState<number | null>(null)
  // Convert size to number
  const starSize = typeof size === 'string' ? (size === 'small' ? 16 : size === 'large' ? 24 : 20) : size

  const fullStars = Math.floor(Number(value))
  const hasHalfStar = !interactive && Number(value) - fullStars >= 0.5
  const stars: React.ReactElement[] = []

  const handleStarClick = (starIndex: number) => {
    if (interactive && onChange && !readonly) {
      onChange(starIndex + 1)
    }
  }

  for (let i = 0; i < outOf; i++) {
    const displayValue = interactive && hoverValue !== null ? hoverValue : value
    const isFilled = interactive ? i < displayValue : i < fullStars
    const starElement = (
      <button
        key={i}
        onClick={() => handleStarClick(i)}
        onMouseEnter={() => interactive && !readonly && setHoverValue(i + 1)}
        onMouseLeave={() => interactive && !readonly && setHoverValue(null)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleStarClick(i)
          }
        }}
        style={{
          cursor: interactive && !readonly ? 'pointer' : 'default',
          display: 'inline-block',
          border: 'none',
          background: 'transparent',
          padding: 0,
        }}
        disabled={!interactive || readonly}
        aria-label={`Rate ${i + 1} star${i === 0 ? '' : 's'}`}
      >
        <Star filled={isFilled} size={starSize} color={color} />
      </button>
    )

    if (!interactive && i === fullStars && hasHalfStar) {
      stars.push(
        <span key={i} style={{ position: 'relative', display: 'inline-block' }}>
          <Star filled={false} size={starSize} color={color} />
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '50%',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Star filled={true} size={starSize} color={color} />
          </div>
        </span>,
      )
    } else {
      stars.push(starElement)
    }
  }

  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, ...starStyle }}>{stars}</span>
}
